// src/lib/NiceSelect.js
// A dropdown that matches the portal instead of the operating system: dark
// rounded panel, hover highlight, check on the current pick, optional group
// headers. Drop-in-ish replacement for a native <select>.
//
//   <NiceSelect value={v} onChange={setV} options={['A','B']} placeholder="Pick…" />
//   options: strings, { value, label }, or { group, options: [...] }
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

const norm = (o) => (typeof o === 'string' ? { value: o, label: o } : o);

export default function NiceSelect({ value, onChange, options = [], placeholder = 'Select…', className = '', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const flat = [];
  const rendered = [];
  options.forEach((o, gi) => {
    if (o && o.group) {
      rendered.push({ header: o.group, key: `g${gi}` });
      (o.options || []).forEach(x => { const n = norm(x); flat.push(n); rendered.push(n); });
    } else {
      const n = norm(o); flat.push(n); rendered.push(n);
    }
  });
  const current = flat.find(o => o.value === value);

  const toggle = () => {
    if (disabled) return;
    if (!open && wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setOpenUp(r.bottom + 280 > (window.innerHeight || 800)); // flip up near the bottom
    }
    setOpen(v => !v);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-gray-800 border rounded-xl text-sm text-left transition-colors ${
          open ? 'border-indigo-600/60' : 'border-gray-700'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-600'} ${current ? 'text-zinc-200' : 'text-zinc-500'}`}
      >
        <span className="truncate">{current ? current.label : placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`absolute left-0 right-0 z-[80] ${openUp ? 'bottom-full mb-1' : 'top-full mt-1'} bg-gray-900 border border-gray-700 rounded-xl shadow-2xl shadow-black/50 max-h-64 overflow-y-auto py-1`}>
          {rendered.map((o, i) => o.header ? (
            <p key={o.key || i} className="px-3.5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600">{o.header}</p>
          ) : (
            <button
              key={`${o.value}-${i}`}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full flex items-center justify-between gap-2 px-3.5 py-2 text-sm text-left transition-colors ${
                o.value === value ? 'text-indigo-300 bg-indigo-600/10' : 'text-zinc-300 hover:bg-gray-800'
              }`}
            >
              <span className="truncate">{o.label}</span>
              {o.value === value && <Check className="w-3.5 h-3.5 shrink-0" />}
            </button>
          ))}
          {flat.length === 0 && <p className="px-3.5 py-2 text-xs text-zinc-600">No options</p>}
        </div>
      )}
    </div>
  );
}
